import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './devphil.css';
import ParticlesBg from 'particles-bg';
import { Navigation } from './navigation';
import { Contact } from './contact';
import Footer from './footer';

import linuxImg     from '../assets/contrib/linux-packages.jpg';
import communityImg from '../assets/contrib/community.jpg';
import aiDeskImg    from '../assets/contrib/ai-desktop.jpg';
import knowledgeImg from '../assets/contrib/knowledge.jpg';
import enterpriseImg from '../assets/contrib/enterprise.jpg';
import aiDataImg    from '../assets/deeptech/ai-data.jpg';
import iotImg       from '../assets/deeptech/iot-cyber.jpg';
import industryImg  from '../assets/deeptech/industry40.jpg';

const PILLARS = [
  {
    img: linuxImg,
    icon: 'fa-code',
    color: '#60a5fa',
    tag: 'Our Philosophy',
    title: 'Open Source Movement',
    body: 'At RedRuby Technologies, open source is more than a development model — it is a commitment to innovation, transparency, and community-driven progress. We believe the future of computing will be shaped by collaborative ecosystems and accessible technologies.',
    points: [
      { icon: 'fa-check-circle', text: 'Transparency in every layer of our stack' },
      { icon: 'fa-check-circle', text: 'Community-first contribution model' },
      { icon: 'fa-check-circle', text: 'Open standards over proprietary lock-in' },
    ],
    flip: false,
  },
  {
    img: communityImg,
    icon: 'fa-users',
    color: '#34d399',
    tag: 'Community',
    title: 'Building with the Community',
    body: 'Our engineering teams are continuous contributors to the Ubuntu and Debian communities, actively supporting and enhancing the Linux ecosystem. We embrace open standards and leverage proven open-source technologies to deliver secure, scalable, and future-ready solutions.',
    points: [
      { icon: 'fa-check-circle', text: 'Active Ubuntu and Debian package contributors' },
      { icon: 'fa-check-circle', text: 'Vendor-neutral, interoperable architectures' },
      { icon: 'fa-check-circle', text: 'Sharing knowledge and frameworks publicly' },
    ],
    flip: true,
  },
  {
    img: aiDeskImg,
    icon: 'fa-desktop',
    color: '#a78bfa',
    tag: 'Innovation',
    title: 'Pioneering AI-Powered Linux',
    body: 'RedRuby Technologies has developed multiple desktop-based applications and system components toward building a next-generation AI-enabled Linux OS. By integrating AI directly into the desktop experience, we envision intelligent, privacy-first computing environments for developers, enterprises, and end users.',
    points: [
      { icon: 'fa-check-circle', text: 'AI deeply integrated into the Linux desktop' },
      { icon: 'fa-check-circle', text: 'Privacy-first, on-device intelligence' },
      { icon: 'fa-check-circle', text: 'Built for developers, enterprises, and end users' },
    ],
    flip: false,
  },
];

const OPEN_TECH = [
  { icon: 'fa-users',        label: 'Community-driven development',                  accent: '#2234ae' },
  { icon: 'fa-exchange',     label: 'Open standards and interoperability',            accent: '#0ea5e9' },
  { icon: 'fa-building-o',   label: 'Vendor-neutral architectures',                  accent: '#7c3aed' },
  { icon: 'fa-eye',          label: 'Privacy and transparency by design',            accent: '#d97706' },
  { icon: 'fa-puzzle-piece', label: 'Reusable and extensible frameworks',            accent: '#db2777' },
  { icon: 'fa-leaf',         label: 'Long-term sustainability through collaboration', accent: '#16a34a' },
];

const CONTRIBUTIONS = [
  { img: linuxImg,      icon: 'fa-linux',     title: 'Linux Packages',      text: 'Contributions to Ubuntu and Debian packages and tooling' },
  { img: aiDeskImg,     icon: 'fa-desktop',   title: 'AI Desktop Apps',     text: 'Desktop applications for AI-enabled Linux environments' },
  { img: communityImg,  icon: 'fa-code-fork', title: 'Community Projects',  text: 'Participation in community-driven projects and initiatives' },
  { img: knowledgeImg,  icon: 'fa-book',      title: 'Knowledge Sharing',   text: 'Sharing knowledge, frameworks, and best practices openly' },
  { img: enterpriseImg, icon: 'fa-bullhorn',  title: 'Enterprise Advocacy', text: 'Advocating open-source adoption across enterprises' },
  { img: iotImg,        icon: 'fa-shield',    title: 'Secure Systems',      text: 'Building secure, open IoT and cyber-resilience tooling' },
];

const DevelopmentPhilosophyPage = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <Navigation />

      {/* ── HEADER ── */}
      <header>
        <div style={{ backgroundColor: '#2C5CDF' }} className="intro" data-aos="fade-up">
          <ParticlesBg type="circle" config={{ num: 6, radius: 18, alpha: 0.5 }} bg={{ zIndex: 0, position: 'absolute', top: 0 }} />
          <div className="overlay">
            <div className="container">
              <div className="row">
                <div className="col-md-8 col-md-offset-2 intro-text" data-aos="fade-up" data-aos-delay="300">
                  <h2 style={{ color: 'white' }}>Our Development Philosophy</h2>
                  <div>
                    <h4 style={{ color: 'white' }}>Code Open. Think Beyond.</h4>
                    <p>Building intelligent Linux ecosystems through continuous open-source contributions and AI innovation.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── PHILOSOPHY PILLARS ── */}
      {PILLARS.map(({ img, icon, color, tag, title, body, points, flip }, i) => (
        <div key={title} className="dp-pillar-section" style={{ ...S.pillarSection, background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
          <div className="container">
            <div className={`dp-pillar-row${flip ? ' flip' : ''}`} style={{ ...S.pillarRow, flexDirection: flip ? 'row-reverse' : 'row' }}>

              {/* Image */}
              <div data-aos={flip ? 'fade-left' : 'fade-right'} className="dp-pillar-img" style={S.pillarImgWrap}>
                <img src={img} alt={title} style={S.pillarImg} />
                <div style={{ ...S.pillarImgBadge, background: color }}>
                  <i className={`fa ${icon}`} style={{ marginRight: 7 }} />
                  {tag}
                </div>
              </div>

              {/* Text */}
              <div data-aos={flip ? 'fade-right' : 'fade-left'} className="dp-pillar-text" style={S.pillarText}>
                <span style={{ ...S.pillarTag, color, background: `${color}15`, border: `1px solid ${color}40` }}>
                  <i className={`fa ${icon}`} style={{ marginRight: 6 }} />
                  {tag}
                </span>
                <h2 style={S.pillarTitle}>{title}</h2>
                <p style={S.pillarBody}>{body}</p>
                <div style={S.pointsList}>
                  {points.map(p => (
                    <div key={p.text} style={S.pointRow}>
                      <i className={`fa ${p.icon}`} style={{ color, fontSize: 15, flexShrink: 0 }} />
                      <span style={S.pointText}>{p.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* ── OPEN TECHNOLOGIES ── */}
      <div className="dp-open-tech-section" style={S.openTechSection}>
        <div className="container">
          <div data-aos="fade-up" style={S.sectionHeader}>
            <span style={S.lightEyebrow}>
              <i className="fa fa-lightbulb-o" style={{ marginRight: 6 }} />
              OPEN TECHNOLOGIES, OPEN INNOVATION
            </span>
            <h2 style={S.sectionHeading}>Our Solutions Are Designed Around</h2>
            <p style={S.sectionSub}>Sustainable innovation thrives in open ecosystems.</p>
          </div>
          <div className="dp-tech-grid" style={S.techGrid}>
            {OPEN_TECH.map(({ icon, label, accent }, i) => (
              <div key={label} data-aos="fade-up" data-aos-delay={i * 60} style={S.techCard}>
                <div style={{ ...S.techIconWrap, background: `${accent}12`, border: `2px solid ${accent}30` }}>
                  <i className={`fa ${icon}`} style={{ color: accent, fontSize: 20 }} />
                </div>
                <span style={S.techLabel}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTRIBUTING BACK ── */}
      <div className="dp-contrib-section" style={S.contribSection}>
        <div className="container">
          <div data-aos="fade-up" style={{ ...S.sectionHeader, marginBottom: 48 }}>
            <span style={S.darkEyebrow}>
              <span style={S.dot} />
              CONTRIBUTING BACK TO THE ECOSYSTEM
            </span>
            <h2 style={S.darkHeading}>Beyond Consuming — We Give Back.</h2>
            <p style={S.darkSub}>
              RedRuby actively contributes to the global open-source community through code, knowledge, and advocacy.
            </p>
          </div>
          <div className="dp-contrib-grid" style={S.contribGrid}>
            {CONTRIBUTIONS.map(({ img, icon, title, text }, i) => (
              <div key={title} data-aos="fade-up" data-aos-delay={i * 80} style={S.contribCard}>
                <div style={S.contribImgWrap}>
                  <img src={img} alt={title} style={S.contribImg} />
                  <div style={S.contribOverlay} />
                </div>
                <div style={S.contribBody}>
                  <div style={S.contribIconWrap}>
                    <i className={`fa ${icon}`} style={{ color: '#34d399', fontSize: 16 }} />
                  </div>
                  <h4 style={S.contribTitle}>{title}</h4>
                  <p style={S.contribText}>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Contact />
      <Footer />
    </div>
  );
};

const S = {
  /* pillar sections */
  pillarSection: {
    padding: '90px 0',
  },
  pillarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 60,
  },
  pillarImgWrap: {
    flex: '1 1 420px',
    position: 'relative',
    borderRadius: 22,
    overflow: 'hidden',
    boxShadow: '0 12px 50px rgba(0,0,0,0.14)',
    maxWidth: 520,
  },
  pillarImg: {
    width: '100%',
    height: 400,
    objectFit: 'cover',
    display: 'block',
  },
  pillarImgBadge: {
    position: 'absolute',
    bottom: 20, left: 20,
    color: '#fff', fontSize: 13, fontWeight: 700,
    padding: '7px 16px', borderRadius: 20,
    display: 'flex', alignItems: 'center',
  },
  pillarText: {
    flex: '1 1 340px',
    maxWidth: 500,
  },
  pillarTag: {
    display: 'inline-flex', alignItems: 'center',
    fontSize: 12, fontWeight: 700, letterSpacing: 0.8,
    textTransform: 'uppercase',
    padding: '5px 14px', borderRadius: 20,
    marginBottom: 16,
  },
  pillarTitle: {
    fontSize: 'clamp(24px, 3vw, 34px)',
    fontWeight: 800, color: '#0f172a',
    lineHeight: 1.2, margin: '0 0 16px',
  },
  pillarBody: {
    fontSize: 15.5, color: '#475569',
    lineHeight: 1.8, margin: '0 0 24px',
  },
  pointsList: {
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  pointRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
  },
  pointText: {
    fontSize: 15, color: '#334155', fontWeight: 500, lineHeight: 1.5,
  },

  /* open tech */
  openTechSection: {
    background: '#ffffff',
    padding: '100px 0',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: 52,
  },
  lightEyebrow: {
    display: 'inline-flex', alignItems: 'center',
    background: '#eff6ff', color: '#1e40af',
    border: '1px solid #bfdbfe',
    borderRadius: 20, padding: '5px 16px',
    fontSize: 11, fontWeight: 700, letterSpacing: 1.1,
    marginBottom: 14,
  },
  sectionHeading: {
    color: '#0f172a', fontSize: 34, fontWeight: 800,
    textTransform: 'uppercase', margin: '8px 0 12px', lineHeight: 1.2,
  },
  sectionSub: {
    color: '#64748b', fontSize: 15.5,
    maxWidth: 460, margin: '0 auto', lineHeight: 1.75,
  },
  techGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20,
  },
  techCard: {
    display: 'flex', alignItems: 'center', gap: 16,
    background: '#f8fafc', border: '1.5px solid #e2e8f0',
    borderRadius: 14, padding: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  techIconWrap: {
    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  techLabel: {
    fontSize: 15.5, fontWeight: 600, color: '#1e293b', lineHeight: 1.4,
  },

  /* contributing back */
  contribSection: {
    backgroundColor: '#2234ae',
    backgroundImage: 'linear-gradient(315deg, #2234ae 0%, #191714 100%)',
    padding: '100px 0',
  },
  darkEyebrow: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 20, padding: '5px 16px',
    color: '#6ee7b7', fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
    marginBottom: 14,
  },
  darkHeading: {
    color: '#fff', fontSize: 34, fontWeight: 800,
    textTransform: 'uppercase', margin: '8px 0 12px', lineHeight: 1.2,
  },
  darkSub: {
    color: 'rgba(255,255,255,0.75)', fontSize: 15.5,
    maxWidth: 480, margin: '0 auto', lineHeight: 1.75,
  },
  contribGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  contribCard: {
    borderRadius: 18, overflow: 'hidden',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  contribImgWrap: {
    position: 'relative', height: 200, overflow: 'hidden',
  },
  contribImg: {
    width: '100%', height: '100%',
    objectFit: 'cover', display: 'block',
  },
  contribOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to top,rgba(0,0,0,0.6) 0%,rgba(0,0,0,0.05) 100%)',
  },
  contribBody: {
    padding: '20px 20px 24px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  contribIconWrap: {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
    background: 'rgba(52,211,153,0.12)',
    border: '1.5px solid rgba(52,211,153,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  contribTitle: {
    fontSize: 16, fontWeight: 700, color: '#fff', margin: 0,
  },
  contribText: {
    fontSize: 14.5, color: 'rgba(255,255,255,0.72)',
    lineHeight: 1.65, margin: 0,
  },
};

export default DevelopmentPhilosophyPage;
